"use client"

/**
 * Banner — two types of banner for system-level and local-level messaging.
 *
 * 1. SystemBanner: full-width strip at the very top of the app (above sidebar).
 *    Used for maintenance notices, feature promotions, global alerts.
 *
 * 2. LocalBanner: inline alert within a page section.
 *    Used for page-specific errors, warnings, and informational messages.
 *
 * Both support: info, warning, error, success, and promo variants.
 * Both are dismissible and accessible (role="alert" / role="status").
 */

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Tip }    from "./tip"

// ─────────────────────────────────────────────────────────────────────────────
// Variant definitions
// ─────────────────────────────────────────────────────────────────────────────

const VARIANT_CONFIG = {
  info: {
    icon: "fa-circle-info",
    prefix: "fa-light",
    role: "status" as const,
    live: "polite" as const,
  },
  warning: {
    icon: "fa-triangle-exclamation",
    prefix: "fa-light",
    role: "alert" as const,
    live: "assertive" as const,
  },
  error: {
    icon: "fa-circle-exclamation",
    prefix: "fa-light",
    role: "alert" as const,
    live: "assertive" as const,
  },
  success: {
    icon: "fa-circle-check",
    prefix: "fa-light",
    role: "status" as const,
    live: "polite" as const,
  },
  promo: {
    icon: "fa-star-christmas text-brand",
    prefix: "fa-duotone fa-solid",
    role: "status" as const,
    live: "polite" as const,
  },
}

// ─────────────────────────────────────────────────────────────────────────────
// SystemBanner — inline at the top of the main content area
// ─────────────────────────────────────────────────────────────────────────────

const systemBannerActionClassName = cn(
  "inline-flex shrink-0 items-center gap-1 text-inherit text-xs font-semibold",
  "underline underline-offset-2 hover:no-underline",
  "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/50",
)

const systemBannerVariants = cva(
  // No `overflow-hidden` on root — it clips promo `boxShadow` (glow). Clip `decorativeOverlay` in a nested layer instead.
  "relative isolate flex rounded-lg border text-sm transition-all",
  {
    variants: {
      variant: {
        /* Prominent defaults — darker shells + light foreground copy for ≥4.5:1 on the fill. */
        info:    "bg-blue-900 text-blue-50 border-blue-950/35 dark:bg-blue-950 dark:text-blue-50 dark:border-blue-900/60",
        warning: "bg-amber-900 text-amber-50 border-amber-950/35 dark:bg-amber-950 dark:text-amber-50 dark:border-amber-900/60",
        error:   "bg-red-900 text-red-50 border-red-950/35 dark:bg-red-950 dark:text-red-50 dark:border-red-900/60",
        success: "bg-emerald-900 text-emerald-50 border-emerald-950/35 dark:bg-emerald-950 dark:text-emerald-50 dark:border-emerald-900/60",
        promo:   "bg-gradient-to-r from-brand/14 via-brand/8 to-brand/5 text-foreground border-brand/22 dark:from-brand/20 dark:via-brand/12 dark:to-brand/7 dark:border-brand/26",
      },
      /** Emphasis — "prominent" uses dark solid fills; "subtle" uses soft tinted bg. */
      emphasis: {
        prominent: "",
        subtle: "",
      },
      /** Action placement: "inline" puts the action to the right; "bottom" puts it below the text */
      actionPosition: {
        // flex-wrap so the action button drops to a second line at 200%+ zoom instead of overflowing
        inline: "flex-wrap items-center gap-3 px-4 py-2.5",
        bottom: "flex-col gap-2 px-4 py-3",
      },
    },
    compoundVariants: [
      { emphasis: "subtle", variant: "info",    class: "bg-blue-500/5 text-blue-800 border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-200 dark:border-blue-500/20" },
      { emphasis: "subtle", variant: "warning", class: "bg-amber-500/5 text-amber-800 border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/20" },
      { emphasis: "subtle", variant: "error",   class: "bg-red-500/5 text-red-800 border-red-500/30 dark:bg-red-500/10 dark:text-red-200 dark:border-red-500/20" },
      { emphasis: "subtle", variant: "success", class: "bg-emerald-500/5 text-emerald-800 border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-500/20" },
      { emphasis: "subtle", variant: "promo",   class: "bg-brand/5 text-foreground border-brand/30 dark:bg-brand/10 dark:border-brand/20" },
    ],
    defaultVariants: { variant: "info", emphasis: "prominent", actionPosition: "inline" },
  }
)

export interface SystemBannerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof systemBannerVariants> {
  /** Banner title (optional — adds a bold heading) */
  title?: string
  /** The banner message */
  children: React.ReactNode
  /** Whether the banner can be dismissed */
  dismissible?: boolean
  /** Callback when dismissed */
  onDismiss?: () => void
  /** Optional action — renders as a link-style button. Use href for server components, onClick for client. */
  action?: {
    label: string
    href?: string
    onClick?: () => void
  }
  /** Where to place the action: "inline" (right side) or "bottom" (below text) */
  actionPosition?: "inline" | "bottom"
  /** Override the default icon */
  icon?: string
  /** Optional absolutely-positioned decorative layer (e.g. `<AiThinkingOverlay />`). */
  decorativeOverlay?: React.ReactNode
}

export function SystemBanner({
  children,
  title,
  variant = "info",
  emphasis = "prominent",
  dismissible = true,
  onDismiss,
  action,
  actionPosition = "inline",
  icon,
  decorativeOverlay,
  className,
  style,
  ...props
}: SystemBannerProps) {
  const [dismissed, setDismissed] = React.useState(false)
  const config = VARIANT_CONFIG[variant ?? "info"]

  function handleDismiss() {
    setDismissed(true)
    onDismiss?.()
  }

  if (dismissed) return null

  const actionEl = action && (
    action.href ? (
      <a
        href={action.href}
        className={systemBannerActionClassName}
        suppressHydrationWarning
      >
        {action.label}
        <i className="fa-light fa-arrow-right text-xs" aria-hidden="true" />
      </a>
    ) : (
      <button type="button" onClick={action.onClick} className={systemBannerActionClassName}>
        {action.label}
        <i className="fa-light fa-arrow-right text-xs" aria-hidden="true" />
      </button>
    )
  )

  /** Outside-only brand halo (no inset fill — glow does not paint inside the banner). */
  const promoOuterShadow =
    emphasis === "subtle"
      ? "0 10px 36px -6px oklch(from var(--brand-color) l c h / 0.2), 0 2px 12px -4px oklch(from var(--brand-color) l c h / 0.12)"
      : "0 14px 48px -8px oklch(from var(--brand-color) l c h / 0.26), 0 4px 18px -4px oklch(from var(--brand-color) l c h / 0.16)"

  return (
    <div
      role={config.role}
      aria-live={config.live}
      className={cn(systemBannerVariants({ variant, emphasis, actionPosition }), className)}
      style={{
        ...(variant === "promo" ? { boxShadow: promoOuterShadow } : null),
        ...style,
      }}
      suppressHydrationWarning
      {...props}
    >
      {decorativeOverlay ? (
        <div
          className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-lg"
          aria-hidden
        >
          {decorativeOverlay}
        </div>
      ) : null}
      {/* Icon */}
      <i
        className={cn(
          config.prefix,
          icon ?? config.icon,
          "relative z-[1] shrink-0 text-sm",
          actionPosition === "bottom" ? "mt-0.5" : "",
        )}
        aria-hidden="true"
      />

      {/* Content + inline action */}
      {actionPosition === "inline" ? (
        <>
          <div className="relative z-[1] min-w-0 flex-1">
            {title && <span className="font-semibold me-1.5">{title}</span>}
            <span className="opacity-90">{children}</span>
          </div>
          {actionEl ? <span className="relative z-[1] inline-flex shrink-0">{actionEl}</span> : null}
        </>
      ) : (
        <div className="relative z-[1] min-w-0 flex-1">
          {title && <p className="font-semibold leading-tight mb-0.5">{title}</p>}
          <p className="opacity-90 leading-relaxed">{children}</p>
          {actionEl && <div className="mt-1.5">{actionEl}</div>}
        </div>
      )}

      {/* Dismiss */}
      {dismissible && (
        <Tip label="Dismiss" side="bottom">
          <button
            type="button"
            aria-label="Dismiss banner"
            onClick={handleDismiss}
            className={cn(
              "relative z-[2] inline-flex size-5 shrink-0 items-center justify-center rounded transition-colors",
              actionPosition === "bottom" ? "absolute top-2.5 right-2.5" : "",
              "hover:bg-current/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <i className="fa-light fa-xmark text-xs" aria-hidden="true" />
          </button>
        </Tip>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// LocalBanner — inline, within page sections
// ─────────────────────────────────────────────────────────────────────────────

const localBannerVariants = cva(
  "flex items-start gap-3 rounded-lg border px-4 py-3 text-sm transition-all",
  {
    variants: {
      variant: {
        info:    "border-blue-500/30 bg-blue-500/5 text-blue-800 dark:bg-blue-500/10 dark:text-blue-200 dark:border-blue-500/20",
        warning: "border-amber-500/30 bg-amber-500/5 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/20",
        error:   "border-red-500/30 bg-red-500/5 text-red-800 dark:bg-red-500/10 dark:text-red-200 dark:border-red-500/20",
        success: "border-emerald-500/30 bg-emerald-500/5 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-500/20",
        promo:   "border-brand/30 bg-brand/5 text-foreground dark:bg-brand/10 dark:border-brand/20",
      },
    },
    defaultVariants: { variant: "info" },
  }
)

export interface LocalBannerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof localBannerVariants> {
  /** Banner title (optional) */
  title?: string
  /** Banner message */
  children: React.ReactNode
  /** Whether the banner can be dismissed */
  dismissible?: boolean
  /** Callback when dismissed */
  onDismiss?: () => void
  /** Optional action — renders as link (href) or button (onClick) */
  action?: {
    label: string
    href?: string
    onClick?: () => void
  }
  /** Optional retry action for error states */
  retry?: {
    label?: string
    onClick: () => void
  }
  /** Override the default icon */
  icon?: string
}

export function LocalBanner({
  children,
  title,
  variant = "info",
  dismissible = false,
  onDismiss,
  action,
  retry,
  icon,
  className,
  ...props
}: LocalBannerProps) {
  const [dismissed, setDismissed] = React.useState(false)
  const config = VARIANT_CONFIG[variant ?? "info"]

  function handleDismiss() {
    setDismissed(true)
    onDismiss?.()
  }

  if (dismissed) return null

  return (
    <div
      role={config.role}
      aria-live={config.live}
      className={cn(localBannerVariants({ variant }), className)}
      {...props}
    >
      {/* Icon */}
      <i
        className={cn(config.prefix, icon ?? config.icon, "text-md shrink-0 mt-0.5")}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <p className="font-semibold leading-tight mb-0.5">{title}</p>
        )}
        <div className="text-sm leading-relaxed opacity-90">{children}</div>

        {/* Actions */}
        {(action || retry) && (
          <div className="flex items-center gap-2 mt-2.5">
            {retry && (
              <Button size="xs" variant="outline" onClick={retry.onClick}>
                <i className="fa-light fa-arrow-rotate-right text-xs" aria-hidden="true" />
                {retry.label ?? "Retry"}
              </Button>
            )}
            {action && (
              action.href ? (
                <a href={action.href} className="inline-flex items-center gap-1 text-xs font-semibold underline underline-offset-2 hover:no-underline">
                  {action.label} <i className="fa-light fa-arrow-right text-xs" aria-hidden="true" />
                </a>
              ) : (
                <Button size="xs" variant="outline" onClick={action.onClick}>
                  {action.label}
                </Button>
              )
            )}
          </div>
        )}
      </div>

      {/* Dismiss */}
      {dismissible && (
        <Tip label="Dismiss" side="left">
          <button
            type="button"
            aria-label="Dismiss"
            onClick={handleDismiss}
            className={cn(
              "inline-flex items-center justify-center size-5 rounded transition-colors shrink-0",
              "hover:bg-current/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            )}
          >
            <i className="fa-light fa-xmark text-xs" aria-hidden="true" />
          </button>
        </Tip>
      )}
    </div>
  )
}
